import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { I18NextModule, I18NextService } from 'angular-i18next';
import type { UserNotification } from '../../actions/user-notifications.actions';
import { NotificationType } from '@modeso/aumico-lib-users-fe/enums/notification-type.enum';

@Component({
    selector: 'notification-drawer',
    templateUrl: './notification-drawer.component.html',
    styleUrls: ['./notification-drawer.component.scss'],
    standalone: true,
    imports: [CommonModule, I18NextModule],
})
export class NotificationDrawerComponent implements OnChanges {
    @Input() public open = false;
    @Input() public loading = false;
    @Input() public notifications: UserNotification[] = [];

    @Output() public close = new EventEmitter<void>();
    @Output() public archive = new EventEmitter<{ notification: UserNotification; archive: boolean }>();
    @Output() public markReadUnread = new EventEmitter<{ notification: UserNotification; read: boolean }>();
    @Output() public openNotification = new EventEmitter<UserNotification>();
    @Output() public markAllAsRead = new EventEmitter<void>();
    @Output() public filterChange = new EventEmitter<string | boolean | undefined>();

    public grouped: { title: string; items: UserNotification[] }[] = [];
    openedFlyout: boolean = false;
    flyoutPosition: { top: number; left: number } = { top: 0, left: 0 };
    filterOptions: { label: string; value: string | boolean | undefined }[] = [];
    filterOptionSelected: string;
    @ViewChild('filterBtnRef', { static: false }) filterBtnRef: ElementRef<HTMLElement>;
    private flyoutElement: HTMLElement | null = null;
    private rtf: Intl.RelativeTimeFormat;
    private dateFormatter: Intl.DateTimeFormat;

    public constructor(private readonly i18nService: I18NextService) {
        const locale = this.resolveLocale();
        this.rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
        this.dateFormatter = new Intl.DateTimeFormat(locale, { dateStyle: 'short', timeStyle: 'short' });
        
        this.filterOptions = [
            { label: 'aumico_user_notifications_filter_read', value: "READ" },
            { label: 'aumico_user_notifications_filter_unread', value: "UNREAD" },
            { label: 'aumico_user_notifications_filter_archived', value: "ARCHIVED" },
        ];
    }

    public onClose(): void {
        this.close.emit();
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if (!this.openedFlyout) return;
        const target = event.target as HTMLElement;
        const btnEl = this.filterBtnRef?.nativeElement;
        const flyoutEl = this.flyoutElement;
        if (btnEl?.contains(target) || flyoutEl?.contains(target)) {
            return;
        }
        this.closeFlyout();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['notifications']) {
            this.grouped = this.buildGroups(this.notifications ?? []);
        }
    }

    private buildGroups(items: UserNotification[]): { title: string; items: UserNotification[] }[] {
        const today: UserNotification[] = [];
        const lastWeek: UserNotification[] = [];
        const older: UserNotification[] = [];

        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
        const startOfWeek = startOfToday - 7 * 24 * 60 * 60 * 1000;

        for (const n of items) {
            const created = new Date(n.createdAt as unknown as string).getTime();
            if (created >= startOfToday) {
                today.push(n);
            } else if (created >= startOfWeek) {
                lastWeek.push(n);
            } else {
                older.push(n);
            }
        }

        const groups: { title: string; items: UserNotification[] }[] = [];
        if (today.length) groups.push({ title: this.i18nService.t('aumico_user_notifications_today') as string, items: today });
        if (lastWeek.length) groups.push({ title: this.i18nService.t('aumico_user_notifications_last_week') as string, items: lastWeek });
        if (older.length) groups.push({ title: this.i18nService.t('aumico_user_notifications_older') as string, items: older });
        return groups;
    }

    public buildMessage(n: UserNotification): string {
        const data = n.data;
        const taskName = data.taskName ?? this.i18nService.t('aumico_user_notifications_task_fallback') as string;
        const companyName = data.companyName ?? '';
        let role = data.role ?? this.i18nService.t('aumico_user_notifications_role_fallback') as string;
        const user = data.triggeredByUserName ?? this.i18nService.t('aumico_user_notifications_someone_fallback') as string;
        const dueDateRaw = data.dueDate;
        const dueDate = dueDateRaw ? new Date(dueDateRaw).toLocaleDateString() : this.i18nService.t('aumico_user_notifications_due_date_fallback') as string;

        if (role.toLowerCase() === "clerk") {
            role = this.i18nService.t('aumico_companies_list_filter_clerk') as string;
        } else if (role.toLowerCase() === "manager") {
            role = this.i18nService.t('aumico_companies_list_filter_mandate_manager') as string;
        }
        switch (n.type) {
            case NotificationType.TASK_ASSIGNED:
                return this.i18nService.t('aumico_user_notifications_message_task_assigned', {
                    taskName: `<b>${taskName}</b>`
                }) as string;
            case NotificationType.COMPANY_ASSIGNED:
                return this.i18nService.t('aumico_user_notifications_message_company_assigned', {
                    companyName: `<b>${companyName || this.i18nService.t('aumico_user_notifications_company_fallback')}</b>`,
                    role: `<b>${role}</b>`
                }) as string;
            case NotificationType.MENTION:
                return this.i18nService.t('aumico_user_notifications_message_mention', {
                    taskName: `<b>${taskName}</b>`,
                    companyName: companyName ? ` (<b>${companyName}</b>)` : '',
                    user: `<b>${user}</b>`
                }) as string;
            // REV-21374 — eine ABCP-Erwaehnung. OHNE diesen Fall fiel sie in den `default`-Zweig
            // und stand als blosse „Benachrichtigung" in der Glocke: das Klick-Ziel stimmte, der
            // Text sagte aber weder wer erwaehnt hat noch wo. Im Code-Review 2026-09-23 als
            // Auflage 2 gefunden.
            //
            // ⚠️ `taskName` gibt es hier NICHT — ein Abschluss-Eintrag hat keine Aufgabe (genau
            // deshalb ist `NOTE_MENTION` ein eigener Typ, siehe SRR F5). Die Meldung nennt
            // stattdessen die ART des Eintrags, und der Klick fuehrt zu ihm.
            case NotificationType.NOTE_MENTION:
                return this.i18nService.t(
                    data.noteKind === 'TODO'
                        ? 'aumico_user_notifications_message_note_mention_todo'
                        : 'aumico_user_notifications_message_note_mention_comment',
                    { user: `<b>${user}</b>` },
                ) as string;
            case NotificationType.TASK_READY_FOR_REVIEW:
                return this.i18nService.t('aumico_user_notifications_message_task_ready_review', {
                    taskName: `<b>${taskName}</b>`
                }) as string;
            case NotificationType.TASK_REJECTED:
                return this.i18nService.t('aumico_user_notifications_message_task_rejected', {
                    taskName: `<b>${taskName}</b>`,
                    user: `<b>${user}</b>`
                }) as string;
            case NotificationType.DEADLINE_APPROACHING:
                return this.i18nService.t('aumico_user_notifications_message_deadline_approaching', {
                    taskName: `<b>${taskName}</b>`,
                    companyName: `<b>${companyName || this.i18nService.t('aumico_user_notifications_company_fallback')}</b>`,
                    dueDate: `<b>${dueDate}</b>`
                }) as string;
            default:
                return this.i18nService.t('aumico_user_notifications_message_default') as string;
        }
    }

    getInitials(fullName: string, type: NotificationType): string {
        if(type === NotificationType.DEADLINE_APPROACHING) {
            return '!!!'
        }
        
        if (!fullName) return '-';
        const words = fullName.trim().split(' ');
        if (words.length > 1) {
            // If there are at least two words, return the first letter of the first word and the first letter of the last word
            return (words[0][0] + words[words.length - 1][0]).toUpperCase();
        } else {
            // Otherwise, return the first two letters of the single word
            return fullName.slice(0, 2).toUpperCase();
        }
    }

    archiveNotification(notification: UserNotification): void {
        // Placeholder for archiving/ un-archiving logic
        this.archive.emit({ notification, archive: !notification.isArchived });
    }

    markAsReadUnread(notification: UserNotification): void {
        // Placeholder for marking as read/unread logic
        this.markReadUnread.emit({ notification, read: !notification.isRead });
    }

    public getReadStateTooltip(notification: UserNotification): string {
        return this.i18nService.t(
            notification.isRead
                ? 'aumico_user_notifications_mark_as_unread'
                : 'aumico_user_notifications_mark_as_read',
        ) as string;
    }

    public getArchiveStateTooltip(notification: UserNotification): string {
        return this.i18nService.t(
            notification.isArchived
                ? 'aumico_user_notifications_unarchive'
                : 'aumico_user_notifications_archive',
        ) as string;
    }

    openNotificationItem(notification: UserNotification): void {
        this.openNotification.emit(notification);
    }

    onOpenNotificationAndMarkRead(notification: UserNotification): void {
        if (!notification.isRead) {
            this.markReadUnread.emit({ notification, read: true });
        }
        this.openNotification.emit(notification);
    }

    onMarkAllAsRead(): void {
        this.markAllAsRead.emit();
    }

    onOpenFilter(event: MouseEvent): void {
        event.stopPropagation();
        this.openedFlyout = !this.openedFlyout;
        if (this.openedFlyout && this.filterBtnRef?.nativeElement) {
            const rect = this.filterBtnRef.nativeElement.getBoundingClientRect();
            this.flyoutPosition = {
                top: rect.bottom + 8,
                left: rect.left - 150,
            };
            setTimeout(() => {
                this.flyoutElement = document.querySelector('.notification-filter-flyout');
            });
        } else {
            this.flyoutElement = null;
        }
    }

    closeFlyout(): void {
        this.openedFlyout = false;
        this.flyoutElement = null;
    }

    onFilterChange(value: string | boolean | undefined): void {
        this.filterOptionSelected = value as string;
        this.filterChange.emit(value);
        this.closeFlyout();
    }

    public getDisplayTime(value: string | Date): string {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
            return '';
        }

        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        if (diffMs < 0) {
            return this.formatDate(date);
        }

        const diffMinutes = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);

        // If within today, show relative minutes/hours
        if (diffHours < 24) {
            if (diffMinutes < 60) {
                return this.rtf.format(-diffMinutes, 'minute');
            }
            return this.rtf.format(-diffHours, 'hour');
        }

        // Otherwise show localized date/time
        return this.formatDate(date);
    }

    private formatDate(date: Date): string {
        return this.dateFormatter.format(date);
    }

    private resolveLocale(): string {
        return this.i18nService?.language || navigator.language || 'en';
    }
}    
